<template>
  <div class="container-fluid py-4">
    <!-- En-tête -->
    <div class="page-header mb-4">
      <div class="header-content">
        <div>
          <h2 class="header-title">Mes devoirs soumis</h2>
          <p class="header-subtitle">Consultez et gérez tous vos devoirs soumis</p>
        </div>
        <router-link to="/soumettre-devoir" class="btn-header-action">
          <i class="fa fa-plus-circle me-2"></i>
          Soumettre un devoir
        </router-link>
      </div>
    </div>

    <!-- Statistiques -->
    <div class="row mb-4 g-4">
      <div class="col-md-3">
        <div class="stat-card card-info">
          <div class="stat-card-inner">
            <div class="stat-icon-wrapper">
              <div class="stat-icon icon-info">
                <i class="fa fa-inbox"></i>
              </div>
            </div>
            <div class="stat-content">
              <div class="stat-label">Total soumis</div>
              <div class="stat-number">{{ stats.total }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card card-warning">
          <div class="stat-card-inner">
            <div class="stat-icon-wrapper">
              <div class="stat-icon icon-warning">
                <i class="fa fa-clock-o"></i>
              </div>
            </div>
            <div class="stat-content">
              <div class="stat-label">En attente</div>
              <div class="stat-number">{{ stats.soumis }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card card-primary">
          <div class="stat-card-inner">
            <div class="stat-icon-wrapper">
              <div class="stat-icon icon-primary">
                <i class="fa fa-pencil"></i>
              </div>
            </div>
            <div class="stat-content">
              <div class="stat-label">En correction</div>
              <div class="stat-number">{{ stats.enCours }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card card-success">
          <div class="stat-card-inner">
            <div class="stat-icon-wrapper">
              <div class="stat-icon icon-success">
                <i class="fa fa-check-circle"></i>
              </div>
            </div>
            <div class="stat-content">
              <div class="stat-label">Corrigés</div>
              <div class="stat-number">{{ stats.corriges }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chargement -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="loading-text mt-3">Chargement...</p>
    </div>

    <!-- État vide -->
    <div v-else-if="devoirs.length === 0" class="modern-card">
      <div class="card-body">
        <div class="empty-state-content">
          <div class="empty-icon">
            <i class="fa fa-inbox"></i>
          </div>
          <h5 class="empty-title">Aucun devoir soumis</h5>
          <p class="empty-text">Commencez par soumettre votre premier devoir</p>
          <router-link to="/soumettre-devoir" class="btn-empty-action">
            <i class="fa fa-plus-circle me-2"></i>
            Soumettre un devoir
          </router-link>
        </div>
      </div>
    </div>
<!-- Remplace toute la boucle des cartes par ÇA -->
<div v-else>
  <div class="row g-3 g-md-4">
    <div v-for="devoir in devoirs" :key="devoir.id" class="col-12 col-md-6 col-lg-4">
      <div class="devoir-card-clean">

        <!-- Titre + statut (sans fond violet) -->
        <div class="card-header-clean">
          <h6 class="title">{{ devoir.composition?.titre }}</h6>
          <span :class="['status-tag', getStatutClass(devoir.statut)]">
            {{ getStatutLabel(devoir.statut) }}
          </span>
        </div>

        <!-- Infos -->
        <div class="card-body-clean">
          <div class="info"><strong>Matière :</strong> {{ devoir.composition?.filiereNiveauMatiere?.matiere?.libelle }}</div>
          <div class="info"><strong>Date de soumission :</strong> {{ formatDateShort(devoir.dateSoumission) }}</div>
          
          <div v-if="devoir.note !== null" class="info note">
            <strong>Note :</strong> 
            <span class="big-note">{{ formatNote(devoir.note) }}/20</span>
          </div>

          <!-- COMMENTAIRE 100% VISIBLE (plus jamais coupé) -->
          <div v-if="devoir.commentaire" class="comment-box">
            <div class="comment-title">Commentaire du professeur</div>
            <div class="comment-text">{{ devoir.commentaire }}</div>
          </div>
        </div>

        <!-- Actions -->
        <div class="card-footer-clean">
          <button @click="viewDevoirPdf(devoir)" class="btn-small">Voir</button>
          <router-link
            v-if="devoir.statut !== 'corrige'"
            :to="`/modifier-devoir/${devoir.id}`"
            class="btn-small edit"
          >Modifier</router-link>
          <button
            v-if="devoir.fichierCorrigeUrl"
            @click="downloadDevoir(devoir, 'corrige')"
            class="btn-small success"
          >Corrigé</button>
        </div>
      </div>
    </div>
</div>
      <!-- Pagination -->
      <nav class="pagination-container mt-4" v-if="totalPages > 1">
        <ul class="pagination-list">
          <li class="pagination-item" :class="{ disabled: currentPage === 1 }">
            <a class="pagination-link" @click="changePage(currentPage - 1)">
              Précédent
            </a>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="pagination-item"
            :class="{ active: page === currentPage }"
          >
            <a class="pagination-link" @click="changePage(page)">{{ page }}</a>
          </li>
          <li class="pagination-item" :class="{ disabled: currentPage === totalPages }">
            <a class="pagination-link" @click="changePage(currentPage + 1)">
              Suivant
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Modal PDF -->
    <div v-if="showPdfModal" class="modal-overlay" @click.self="closePdfModal">
      <div class="modal-dialog-pdf">
        <div class="modal-content-modern">
          <div class="modal-header-pdf">
            <h5 class="modal-title">
              <i class="fa fa-file-pdf-o me-2"></i>{{ currentPdfTitle }}
            </h5>
            <button type="button" class="btn-close-modal" @click="closePdfModal">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="modal-body-pdf">
            <iframe :src="currentPdfUrl" class="pdf-iframe" title="Visualiseur PDF"></iframe>
          </div>
          <div class="modal-footer-pdf">
            <!-- <a :href="currentPdfUrl" target="_blank" class="btn-modal-action">
              <i class="fa fa-external-link me-2"></i>
              Ouvrir dans un nouvel onglet
            </a> -->
            <button type="button" class="btn-modal-secondary" @click="closePdfModal">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { success, error } from "../../utils/utils";
import ApiService from "../../services/ApiService";

export default defineComponent({
  name: "MesDevoirs",
  setup() {
    
    const devoirs = ref<any[]>([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const limit = ref(10);
    const totalPages = ref(0);
    const totalElements = ref(0);
    const showPdfModal = ref(false);
    const currentPdfUrl = ref('');
    const currentPdfTitle = ref('');

    const stats = computed(() => {
      return {
        total: devoirs.value.length,
        soumis: devoirs.value.filter(d => d.statut === 'soumis').length,
        enCours: devoirs.value.filter(d => d.statut === 'en_cours').length,
        corriges: devoirs.value.filter(d => d.statut === 'corrige').length,
      };
    });

    onMounted(() => {
      fetchDevoirs();
    });

    const formatNote = (note: number) => {
  // Supprime les décimaux inutiles : 16.00 → 16 | 15.50 → 15.5
  return Number(note).toFixed(2).replace(/\.00$/, '');
};

const formatDateShort = (date: string) => {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  });
};

    const fetchDevoirs = async () => {
      loading.value = true;
      try {
        console.log("Appel à /devoirs/mes-devoirs...");
        
        const response = await ApiService.get(
          `/devoirs/mes-devoirs?page=${currentPage.value}&limit=${limit.value}`
        );
        
        console.log(" Réponse reçue:", response.data);

        if (response.data.data) {
          devoirs.value = response.data.data.data || [];
          totalPages.value = response.data.data.totalPages || 1;
          totalElements.value = response.data.data.totalElements || 0;
        }
      } catch (err: any) {
        console.error("Erreur lors du chargement des devoirs:", err);
        console.error("Détails:", err.response?.data);
        error(err.response?.data?.message || "Impossible de charger vos devoirs");
      } finally {
        loading.value = false;
      }
    };

    const changePage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchDevoirs();
      }
    };

    const formatDate = (date: string) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getStatutClass = (statut: string) => {
      switch (statut) {
        case 'soumis': return 'status-warning';
        case 'en_cours': return 'status-primary';
        case 'corrige': return 'status-success';
        default: return 'status-secondary';
      }
    };

    const getStatutLabel = (statut: string) => {
      switch (statut) {
        case 'soumis': return 'En attente';
        case 'en_cours': return 'En correction';
        case 'corrige': return 'Corrigé';
        default: return statut;
      }
    };

    const viewDevoirPdf = async (devoir: any) => {
  try {
    // Utiliser l'API de téléchargement pour obtenir le blob
    const response = await ApiService.get(
      `/devoirs/${devoir.id}/download?type=original`,
      { responseType: 'blob' }
    );
   
    // Créer un URL blob pour la visualisation
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
   
    currentPdfUrl.value = blobUrl;
    currentPdfTitle.value = devoir.fichierNom || 'Devoir';
    showPdfModal.value = true;
    document.body.style.overflow = 'hidden';
  } catch (err: any) {
    console.error("Erreur lors de la visualisation du PDF:", err);
    error(err.response?.data?.message || "Impossible de visualiser le fichier");
  }
};

    const closePdfModal = () => {
      if (currentPdfUrl.value.startsWith('blob:')) {
        window.URL.revokeObjectURL(currentPdfUrl.value);
      }
      showPdfModal.value = false;
      currentPdfUrl.value = '';
      currentPdfTitle.value = '';
      document.body.style.overflow = '';
    };

    const downloadDevoir = async (devoir: any, type: 'original' | 'corrige') => {
      try {
        const response = await ApiService.get(
          `/devoirs/${devoir.id}/download?type=${type}`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', type === 'corrige' ? devoir.fichierCorrigeNom : devoir.fichierNom);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        success(`Téléchargement du fichier en cours...`);
      } catch (err) {
        console.error("Erreur téléchargement", err);
        error("Impossible de télécharger le fichier");
      }
    };

    return {
      devoirs,
      loading,
      currentPage,
      limit,
      totalPages,
      totalElements,
      stats,
      fetchDevoirs,
      changePage,
      formatDate,
      getStatutClass,
      getStatutLabel,
      downloadDevoir,
      showPdfModal,
      currentPdfUrl,
      currentPdfTitle,
      viewDevoirPdf,
      closePdfModal,
      formatNote,
      formatDateShort,
    };
  },
});
</script>

<style scoped>
.devoir-card-clean {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  transition: all 0.2s ease;
  overflow: hidden;
}

.devoir-card-clean:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.12);
}

.card-header-clean {
  padding: 0.9rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.status-tag {
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Statuts propres */
.status-warning { background: #fffbeb; color: #a16207; }
.status-primary { background: #dbeafe; color: #1d4ed8; }
.status-success { background: #d1fae5; color: #059669; }

.card-body-clean {
  padding: 0.9rem 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1f2937;
}

.info {
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note { font-weight: 600; margin-top: 0.6rem; padding-top: 0.6rem; border-top: 1px dashed #e2e8f0; }
.big-note { color: #059669; font-size: 1.2rem; }

.comment-box {
  margin-top: 0.8rem;
  padding: 0.8rem;
  background: #f0f9ff;
  border-radius: 10px;
  border-left: 4px solid #0ea5e9;
  font-size: 0.875rem;
}

.comment-title {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
}

.comment-text {
  margin: 0;
  color: #0c4a6e;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap; /* ← C'EST ÇA QUI SAUVE TOUT */
}

.card-footer-clean {
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-small {
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.btn-small { background: #e0e7ff; color: #4338ca; }
.btn-small:hover { background: #c7d2fe; }

.edit { background: #fffbeb; color: #d97706; }
.edit:hover { background: #fde68a; }

.success { background: #d1fae5; color: #059669; }
.success:hover { background: #a7f3d0; }
.btn-edit:hover { background: #fffbeb; color: #d97706; }
.btn-success:hover { background: #d1fae5; color: #059669; }
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 1.75rem;
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.header-subtitle {
  margin: 0.375rem 0 0 0;
  opacity: 0.9;
  font-size: 0.875rem;
}

.btn-header-action {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
}

.btn-header-action:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  color: white;
}

.stat-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.stat-card-inner {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.icon-warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(251, 191, 36, 0.4);
}

.icon-info {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(96, 165, 250, 0.4);
}

.icon-primary {
  background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(129, 140, 248, 0.4);
}

.icon-success {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(52, 211, 153, 0.4);
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.loading-text {
  color: #6b7280;
  font-size: 1rem;
}

.modern-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-body {
  padding: 1.5rem;
}

.empty-state-content {
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0 0 2rem 0;
}

.btn-empty-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.btn-empty-action:hover {
  background: #5568d3;
  transform: translateY(-2px);
  color: white;
}

.devoir-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.devoir-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.devoir-header {
  padding: 1rem;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.devoir-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  flex: 1;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #fde047;
}

.status-primary {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.status-success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.status-secondary {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  color: #374151;
  border: 1px solid #d1d5db;
}

.devoir-body {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.info-icon {
  color: #6b7280;
  font-size: 1rem;
  width: 20px;
  text-align: center;
}

.info-label {
  color: #6b7280;
  font-weight: 500;
}

.info-value {
  color: #111827;
  font-weight: 600;
}

.note-section {
  margin-top: 0.25rem;
  padding: 0.75rem;
  background: #d4edda;
  border-radius: 10px;
  border-left: 4px solid #28a745;
}

.note-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #155724;
  font-weight: 500;
  font-size: 0.875rem;
}

.note-value {
  font-size: 1.125rem;
  color: #28a745;
  font-weight: 700;
}

.comment-section {
  margin-top: 0.25rem;
  padding: 0.75rem;
  background: #d1ecf1;
  border-radius: 10px;
  border-left: 4px solid #17a2b8;
}

.comment-label {
  font-weight: 600;
  color: #0c5460;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.comment-text {
  margin: 0;
  color: #0c5460;
  font-size: 0.875rem;
  line-height: 1.5;
}

.devoir-footer {
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-view {
  background: white;
  color: #667eea;
  border-color: #667eea;
}

.btn-view:hover {
  background: #667eea;
  color: white;
}

.btn-edit {
  background: white;
  color: #f59e0b;
  border-color: #f59e0b;
}

.btn-edit:hover {
  background: #f59e0b;
  color: white;
}

.btn-download {
  background: white;
  color: #10b981;
  border-color: #10b981;
}

.btn-download:hover {
  background: #10b981;
  color: white;
}

.pagination-container {
  display: flex;
  justify-content: center;
}

.pagination-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0;
}

.pagination-item.disabled .pagination-link {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.pagination-item.active .pagination-link {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.875rem;
}

.pagination-link:hover:not(.disabled) {
  background: #f9fafb;
  border-color: #667eea;
  color: #667eea;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog-pdf {
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
}

.modal-content-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header-pdf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.btn-close-modal {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.25rem;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body-pdf {
  padding: 0;
  flex: 1;
  overflow: hidden;
}

.pdf-iframe {
  width: 100%;
  height: 70vh;
  border: none;
}

.modal-footer-pdf {
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
}

.btn-modal-action {
  background: #667eea;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.btn-modal-action:hover {
  background: #5568d3;
  transform: translateY(-1px);
  color: white;
}

.btn-modal-secondary {
  background: #f3f4f6;
  color: #374151;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-modal-secondary:hover {
  background: #e5e7eb;
}

@media (max-width: 768px) {
  .page-header {
    padding: 1.25rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .header-title {
    font-size: 1.25rem;
    text-align: center;
  }

  .btn-header-action {
    width: 100%;
    justify-content: center;
  }

  .stat-card-inner {
    padding: 1.25rem;
  }

  .stat-number {
    font-size: 1.75rem;
  }

  .devoir-card {
    margin-bottom: 1rem;
  }

  .devoir-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .devoir-footer {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
  }

  .pdf-iframe {
    height: 60vh;
  }
}
</style>